if (!customElements.get('mo-reading-sample')) {
  customElements.define('mo-reading-sample', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.trigger = this.querySelector('[data-sample-trigger]');
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('canvas');
      this.viewport = this.querySelector('.mo-reading-sample__viewport');
      this.status = this.querySelector('[data-sample-status]');
      this.pageNumber = 1;
      this.zoom = 1;
      this.generation = 0;
      if (!this.trigger || !this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (this.dialog.open) return;
        history.pushState({ ...history.state, moReadingSample: this.id }, '', '#leseprobe');
        this.open();
      }, options);
      this.querySelector('[data-sample-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.cleanup(), options);
      window.addEventListener('popstate', () => {
        if (history.state?.moReadingSample === this.id) {
          if (!this.dialog.open) this.open();
        } else if (this.dialog.open) this.dialog.close();
      }, options);
      for (const [selector, delta] of [['previous', -1], ['next', 1]]) {
        this.querySelector(`[data-sample-${selector}]`).addEventListener('click', () => {
          if (!this.pdf || this.rendering) return;
          this.pageNumber = Math.min(this.pdf.numPages, Math.max(1, this.pageNumber + delta));
          this.renderPage();
        }, options);
      }
      for (const [selector, delta] of [['zoom-out', -.25], ['zoom-in', .25]]) {
        this.querySelector(`[data-sample-${selector}]`).addEventListener('click', () => {
          if (!this.pdf || this.rendering) return;
          this.zoom = Math.min(2, Math.max(1, this.zoom + delta));
          this.renderPage();
        }, options);
      }
      this.resize = new ResizeObserver(() => {
        const width = this.viewport.clientWidth;
        if (width && width !== this.lastWidth && this.pdf && this.dialog.open && !this.rendering) this.renderPage(false);
      });
      this.resize.observe(this.viewport);
    }
    async open() {
      const generation = ++this.generation;
      this.scrollPosition = window.scrollY;
      this.oldOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.status.hidden = false;
      this.status.textContent = this.loadingText ||= this.status.textContent;
      this.canvas.hidden = true;
      this.pageNumber = 1;
      this.zoom = 1;
      this.updateControls();
      try {
        const lib = await import(this.dataset.renderer);
        if (generation !== this.generation || !this.dialog.open) return;
        lib.GlobalWorkerOptions.workerSrc = this.dataset.worker;
        this.loadingTask = lib.getDocument({ url: this.dataset.pdfUrl, isEvalSupported: false, disableAutoFetch: true, disableStream: true, useSystemFonts: true });
        const pdf = await this.loadingTask.promise;
        if (generation !== this.generation || !this.dialog.open) { await pdf.destroy(); return; }
        this.pdf = pdf;
        this.querySelector('[data-sample-count]').textContent = pdf.numPages;
        await this.renderPage();
      } catch (error) {
        if (generation === this.generation && this.dialog.open) this.showError();
      }
    }
    updateControls() {
      const unavailable = !this.pdf || this.rendering;
      this.querySelector('[data-sample-previous]').disabled = unavailable || this.pageNumber <= 1;
      this.querySelector('[data-sample-next]').disabled = unavailable || this.pageNumber >= this.pdf?.numPages;
      this.querySelector('[data-sample-zoom-out]').disabled = unavailable || this.zoom <= 1;
      this.querySelector('[data-sample-zoom-in]').disabled = unavailable || this.zoom >= 2;
    }
    async renderPage(resetScroll = true) {
      if (!this.pdf || this.rendering || !this.dialog.open) return;
      const generation = this.generation;
      this.rendering = true;
      this.updateControls();
      try {
        const page = await this.pdf.getPage(this.pageNumber);
        if (generation !== this.generation) return;
        this.lastWidth = this.viewport.clientWidth;
        const available = Math.max(200, this.lastWidth - (matchMedia('(max-width:749px)').matches ? 16 : 24));
        const original = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: Math.min(available, 800) / original.width * this.zoom });
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        this.canvas.width = Math.ceil(viewport.width * ratio);
        this.canvas.height = Math.ceil(viewport.height * ratio);
        this.canvas.style.width = `${viewport.width}px`;
        this.canvas.style.height = `${viewport.height}px`;
        this.renderTask = page.render({ canvasContext: this.canvas.getContext('2d'), viewport, transform: ratio === 1 ? null : [ratio,0,0,ratio,0,0] });
        await this.renderTask.promise;
        if (generation !== this.generation) return;
        this.querySelector('[data-sample-page]').textContent = this.pageNumber;
        this.canvas.hidden = false;
        this.status.hidden = true;
        if (resetScroll) { this.viewport.scrollTop = 0; this.viewport.scrollLeft = 0; }
        await this.readAccessibleText(page, generation);
        page.cleanup();
      } catch (error) {
        if (generation === this.generation && this.dialog.open) this.showError();
      } finally {
        if (generation === this.generation) { this.rendering = false; this.updateControls(); }
      }
    }
    async readAccessibleText(page, generation) {
      const target = this.querySelector('[data-sample-text]');
      target.textContent = '';
      let reader;
      try {
        reader = page.streamTextContent().getReader();
        const parts = [];
        while (true) {
          const { value, done } = await reader.read();
          if (generation !== this.generation) { await reader.cancel(); return; }
          if (done) break;
          for (const item of value.items) parts.push(item.str + (item.hasEOL ? '\n' : ' '));
        }
        target.textContent = parts.join('');
      } catch (error) {
        if (generation === this.generation) target.textContent = this.querySelector('.mo-reading-sample__footer').textContent.trim();
      } finally {
        reader?.releaseLock();
      }
    }
    showError() {
      this.status.textContent = this.dialog.dataset.error;
      this.status.hidden = false;
      this.canvas.hidden = true;
    }
    close() {
      if (history.state?.moReadingSample === this.id) history.back();
      else this.dialog.close();
    }
    cleanup() {
      ++this.generation;
      this.renderTask?.cancel();
      this.loadingTask?.destroy().catch(() => {});
      this.loadingTask = null;
      this.pdf = null;
      this.rendering = false;
      this.canvas.width = this.canvas.height = 0;
      this.querySelector('[data-sample-text]').textContent = '';
      document.documentElement.style.overflow = this.oldOverflow || '';
      this.trigger.focus({ preventScroll: true });
      window.scrollTo({ top: this.scrollPosition, behavior: 'instant' });
      this.updateControls();
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.resize?.disconnect();
      this.abort?.abort();
    }
  });
}
